import { ERentioFormField } from "@rentiohq/shared-frontend/dist/components/components/Form";
import { IUiSchema } from "@rentiohq/shared-frontend/dist/forms/forms.types";
import { getValue } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { JSONSchema6 } from "json-schema";
import { FormValidation } from "react-native-jsonschema-form-test";
import { EField } from "./schema.rentmatchScoreBroker.types";

/**
 * A perfect score may not ask for less than the rent itself.
 *
 * Stricter than the reader, which only refuses a ratio that is not a positive
 * number. Deliberate: the screen can stop a broker typing something the reader
 * would accept, and the other copies must not mirror this one into their
 * readers or their verdicts would differ from ours.
 */
const MIN_RATIO = 1;

export const getSchema =
  (initialValues: { [key: string]: any }) =>
  (defaultValues: { [key: string]: any }): JSONSchema6 => ({
    required: [],
    properties: {
      // No "type" here, deliberately. This form's schema library reads a
      // declared object or array type as something to rebuild from the
      // schema's own properties, of which this field has none, so a declared
      // type replaces the stored table with an empty one.
      [EField.IncomeRanges]: {
        default: getValue(EField.IncomeRanges, initialValues, defaultValues),
      },
    },
  });

/**
 * The whole table has to be usable, or none of it is saved.
 *
 * The same predicates the reader applies, so the two cannot disagree about what
 * a usable table is: one bad row discards the whole thing there, and the office
 * silently falls back to the built-in scale. Without this, an emptied field
 * would save cleanly and then score every property on that scale while the
 * screen still showed the broker the table they thought they had set.
 *
 * Nothing is converted on the way in either. An emptied number field arrives as
 * null, and Number(null) is a perfectly finite 0, which as a ratio would hand
 * every candidate a perfect income score.
 */
const isUsableEntry = (value: any) =>
  typeof value === "number" && isFinite(value) && value > 0;

export const getValidate =
  () =>
  (formData: any, errors: FormValidation): FormValidation => {
    const rows = formData[EField.IncomeRanges] || [];
    const addError = (message: string) =>
      errors[EField.IncomeRanges]?.addError(message);

    if (rows.length < 1) {
      addError(
        getLocalizedText("broker.rentmatch.income_ranges.error.required"),
      );
    }

    rows.forEach((row: any) => {
      // Every row is a point on a curve, so every row needs a rent. There is no
      // open-ended row any more: a point without a rent anchors nothing.
      if (!isUsableEntry(row?.maxRent)) {
        addError(
          getLocalizedText("broker.rentmatch.income_ranges.error.required"),
        );
      }

      if (!isUsableEntry(row?.ratio)) {
        addError(
          getLocalizedText("broker.rentmatch.income_ranges.error.required"),
        );

        return;
      }

      if (row.ratio < MIN_RATIO) {
        addError(
          getLocalizedText("broker.rentmatch.income_ranges.error.ratio"),
        );
      }
    });

    // Strictly increasing. Two points at the same rent would ask for a vertical
    // line, and points out of order would run the curve backwards between them.
    const rents = rows.map((row: any) => row?.maxRent).filter(isUsableEntry);

    const isAscending = rents.every(
      (rent: number, index: number) => index === 0 || rent > rents[index - 1],
    );

    if (!isAscending) {
      addError(getLocalizedText("broker.rentmatch.income_ranges.error.order"));
    }

    return errors;
  };

export const uiSchema = (): IUiSchema<EField> => ({
  [EField.IncomeRanges]: {
    "ui:field": ERentioFormField.IncomeRanges,
    title: getLocalizedText("broker.rentmatch.income_ranges.title"),
  },
});
